import { z } from 'zod';
export const setupStatusSchema = z.object({
  projectId: z.uuid(),
  audience: z.enum(['staff', 'customer']),
  savedRecords: z.number().int().nonnegative(),
  steps: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      page: z.string().nullable(),
      state: z.enum(['done', 'next', 'blocked']),
      detail: z.string(),
    }),
  ),
  provider: z
    .object({
      status: z.enum(['blocked', 'live_unverified']),
      model: z.string(),
      blockers: z.array(z.string()),
    })
    .nullable(),
  monitor: z
    .object({
      configuredSources: z.number().int().nonnegative(),
      persistence: z.enum(['unconfigured', 'configured']),
      liveVerified: z.literal(false),
    })
    .nullable(),
});
export type SetupStatus = z.infer<typeof setupStatusSchema>;
export function setupSteps(input: {
  audience: 'staff' | 'customer';
  actions: readonly string[];
  skus: number;
  records: readonly { kind: string; state: string }[];
  order: boolean;
  readBundles?: readonly string[];
  people?: 'configured' | 'missing' | 'unknown';
  qualifiedMaterial?: boolean;
  qualifiedCost?: boolean;
}) {
  const seen = (kind: string) => input.records.some((r) => r.kind === kind);
  const author = input.actions.includes('create');
  return [
    {
      key: 'context',
      label: 'ลูกค้า แบรนด์ โครงการ และสินค้า',
      page: 'brief',
      state: input.skus ? 'done' : 'blocked',
      detail: input.skus ? 'พบสินค้าในโครงการที่บันทึกแล้ว' : 'ให้ผู้มีสิทธิ์เพิ่มสินค้าในโครงการ',
    },
    {
      key: 'brief',
      label: 'บันทึกความต้องการ',
      page: 'brief',
      state: seen('brief') ? 'done' : author ? 'next' : 'blocked',
      detail: seen('brief')
        ? 'มีข้อมูลที่บันทึกแล้ว'
        : 'เพิ่มข้อมูลด้วยตนเองได้ แม้ AI ยังไม่เชื่อมต่อ',
    },
    {
      key: 'reference',
      label: 'สินค้าอ้างอิงและแนวทางพัฒนา',
      page: 'development',
      state: seen('reference_product') ? 'done' : author ? 'next' : 'blocked',
      detail: 'ตรวจข้อความ ภาพ และแหล่งที่มา · AI เสนอได้เฉพาะร่างที่ต้องทบทวน',
    },
    {
      key: 'sample',
      label: 'ทบทวนตัวอย่างฉบับที่แชร์',
      page: 'samples',
      state: seen('sample') ? 'done' : 'blocked',
      detail: 'การยอมรับตัวอย่างแยกจากการอนุญาตผลิต',
    },
    ...(input.audience === 'staff'
      ? [
          {
            key: 'people',
            label: 'ผู้รับผิดชอบและขอบเขตสิทธิ์',
            page: input.actions.includes('admin') ? 'admin' : null,
            state:
              input.people === 'configured'
                ? 'done'
                : input.actions.includes('admin')
                  ? 'next'
                  : 'blocked',
            detail:
              input.people === 'configured'
                ? 'พบผู้รับผิดชอบที่ยังมีสิทธิ์ในข้อมูลที่อนุญาตให้อ่าน'
                : input.people === 'missing'
                  ? 'ยังขาดผู้รับผิดชอบ ให้ผู้ดูแลจัดเตรียมตามขอบเขตที่อนุมัติ'
                  : 'ยังไม่ทราบ ต้องให้เจ้าของสิทธิ์ที่อ่านข้อมูลบุคลากรได้ตรวจสอบ',
          },
          {
            key: 'qualified-material',
            label: 'สเปกและแหล่งวัตถุดิบที่ทบทวนแล้ว',
            page: input.readBundles?.includes('technical') ? 'materials' : null,
            state: input.qualifiedMaterial
              ? 'done'
              : input.readBundles?.includes('technical')
                ? 'next'
                : 'blocked',
            detail: input.qualifiedMaterial
              ? 'พบสเปกฉบับที่มีผลทบทวนเทคนิคผ่าน ไม่ใช้เพียงการมีร่าง'
              : 'ยังไม่ยืนยันสเปกที่ผ่านการทบทวน ให้ผู้รับผิดชอบวัตถุดิบตรวจแหล่งและเกรด',
          },
          {
            key: 'qualified-cost',
            label: 'ต้นทุนและแหล่งราคาที่ทบทวนแล้ว',
            page: input.readBundles?.includes('cost') ? 'costs' : null,
            state: input.qualifiedCost
              ? 'done'
              : input.readBundles?.includes('cost')
                ? 'next'
                : 'blocked',
            detail: input.qualifiedCost
              ? 'พบต้นทุนฉบับคำนวณที่มีผลทบทวนพาณิชย์ผ่าน'
              : 'ยังไม่ยืนยันต้นทุนและแหล่งราคา ให้ผู้มีสิทธิ์ต้นทุนตรวจข้อมูล ไม่สรุปจากร่าง',
          },
          {
            key: 'handover',
            label: 'ตรวจข้อมูลที่ยืนยันไว้',
            page: 'handover',
            state: input.order ? 'done' : 'blocked',
            detail: 'ยืนยันเชิงพาณิชย์แยกจากการอนุญาตโรงงานและการปล่อย QA',
          },
        ]
      : []),
  ] as SetupStatus['steps'];
}
